// ---------------------------------------------------------------------------
// ListView — workspaces list/picker rendered inside the drawer.
// ---------------------------------------------------------------------------

import {
  Badge,
  Body1,
  Button,
  Card,
  CardHeader,
  Input,
  Spinner,
  Text,
  Toolbar,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import {
  bundleIcon,
  Add20Filled,
  Add20Regular,
  Settings20Filled,
  Settings20Regular,
  Search20Regular,
  Dismiss20Regular,
} from '@fluentui/react-icons';
import { useRef, useState } from 'react';

import { useWorkspaceContext } from '../../context/WorkspaceContext.js';
import { useWorkspaceDrawer } from '../../context/WorkspaceDrawerContext.js';
import { useWorkspaces } from '../../hooks/useWorkspaces/useWorkspaces.js';
import { useUserBi } from '../../telemetry/hooks/useUserBi/useUserBi.js';
import {
  ActionScenario,
  ActionScenarioType,
  PanelRegion,
  PanelType,
} from '../../telemetry/userbi.js';
import type { WorkspaceSummary } from '../../types/workspace.js';
import { filterWorkspaces } from '../../utils/filterWorkspaces.js';

import { useStyles } from './WorkspaceDrawer.style.js';

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
const AddIcon = bundleIcon(Add20Filled, Add20Regular);
const SettingsIcon = bundleIcon(Settings20Filled, Settings20Regular);
/* eslint-enable @typescript-eslint/no-unsafe-assignment */

const EDIT_TOOLTIP_LABEL = 'Edit';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function ListView() {
  const styles = useStyles();
  const { workspaces, isLoading, error } = useWorkspaces();
  const { activeName, setActiveName } = useWorkspaceContext();
  const { open, close } = useWorkspaceDrawer();
  const { recordAction } = useUserBi();
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const filteredWorkspaces = filterWorkspaces(workspaces, query, true);

  const updateQuery = (value: string) => {
    if (query.trim() === '' && value.trim() !== '') {
      recordAction({
        panelType: PanelType.WorkspaceDrawer,
        panelRegion: PanelRegion.Overlay,
        actionScenario: ActionScenario.SearchWorkspaces,
        actionScenarioType: ActionScenarioType.Search,
        data: { 'Usage.Source': 'drawer' },
      });
    }
    setQuery(value);
  };

  const handleSelect = (w: WorkspaceSummary) => {
    setActiveName(w.name);
    close();
  };

  return (
    <Card
      appearance="subtle"
      className={mergeClasses(styles.body, styles.listBody)}
    >
      <div className={styles.listControls}>
        <Toolbar className={styles.listActions}>
          <Button
            appearance="primary"
            icon={<AddIcon />}
            onClick={() => open({ kind: 'create' })}
          >
            Create workspace
          </Button>
        </Toolbar>
        <Input
          className={styles.search}
          input={{ ref: searchRef, className: styles.searchInput }}
          type="search"
          aria-label="Find workspace"
          placeholder="Find workspace..."
          contentBefore={<Search20Regular />}
          contentAfter={
            query !== '' ? (
              <Button
                appearance="transparent"
                size="small"
                icon={<Dismiss20Regular />}
                aria-label="Clear workspace search"
                onClick={() => {
                  updateQuery('');
                  searchRef.current?.focus();
                }}
              />
            ) : undefined
          }
          value={query}
          onChange={(_event, data) => updateQuery(data.value)}
        />
        {!isLoading && error === null && (
          <Text className={styles.resultCount} role="status">
            {filteredWorkspaces.length}{' '}
            {filteredWorkspaces.length === 1 ? 'workspace' : 'workspaces'}
          </Text>
        )}
      </div>

      {isLoading && <Spinner label="Loading workspaces…" />}

      {!isLoading && error !== null && (
        <Card className={mergeClasses(styles.emptyState, styles.errorState)}>
          <Body1>Failed to load workspaces: {error}</Body1>
        </Card>
      )}

      {!isLoading && error === null && workspaces.length === 0 && (
        <Card className={styles.emptyState}>
          <Body1>You don&apos;t have any workspaces yet.</Body1>
          <Button
            appearance="primary"
            icon={<AddIcon />}
            onClick={() => open({ kind: 'create' })}
          >
            Create your first workspace
          </Button>
        </Card>
      )}

      {!isLoading &&
        error === null &&
        workspaces.length > 0 &&
        filteredWorkspaces.length === 0 && (
          <Card className={styles.emptyState}>
            <Body1>No matching workspaces</Body1>
            <Button
              onClick={() => {
                updateQuery('');
                searchRef.current?.focus();
              }}
            >
              Clear search
            </Button>
          </Card>
        )}

      {!isLoading && error === null && filteredWorkspaces.length > 0 && (
        <Card appearance="subtle" className={styles.list}>
          {filteredWorkspaces.map((w) => {
            const isActive = w.name === activeName;
            const lastModifiedDate = w.systemProperties?.lastModifiedDate;
            return (
              <Card
                key={w.name}
                className={mergeClasses(
                  styles.card,
                  isActive && styles.cardActive,
                )}
                role="button"
                tabIndex={0}
                aria-current={isActive ? 'true' : undefined}
                onClick={() => handleSelect(w)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(w);
                  }
                }}
              >
                <CardHeader
                  className={styles.cardHeader}
                  header={
                    <Text className={styles.cardName}>
                      {w.displayName ?? w.name}
                    </Text>
                  }
                  description={
                    <Text className={styles.cardMeta}>
                      {w.name}
                      {lastModifiedDate && (
                        <> · last modified {formatDate(lastModifiedDate)}</>
                      )}
                    </Text>
                  }
                  action={
                    <Toolbar className={styles.cardRight}>
                      <Badge appearance="outline">
                        {w.accessControl == null ? 'Public' : 'Private'}
                      </Badge>
                      <Tooltip
                        content={EDIT_TOOLTIP_LABEL}
                        relationship="label"
                      >
                        <Button
                          appearance="subtle"
                          icon={<SettingsIcon />}
                          aria-label={`Settings for ${w.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            open({ kind: 'settings', name: w.name });
                          }}
                        />
                      </Tooltip>
                    </Toolbar>
                  }
                />
                {w.description && (
                  <Text className={styles.cardDescription}>
                    {w.description}
                  </Text>
                )}
              </Card>
            );
          })}
        </Card>
      )}
    </Card>
  );
}
